import React from 'react'

import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { lazyWithVersionCheck } from '@libs/typescript-utils'

import { getDefaultCurrencyName } from '@helpers'

import type { LoadingReportProps } from './elements/LoadingReports'
import { LoadingReport, LoadingReportError, ReportLayouts } from './elements/LoadingReports'
import type { BackendReportData, ReportComponentProps } from './types'
import { ReportNames } from './types'

function retryLazyImport<ReportData>(
  importFn: () => Promise<{
    default: React.ComponentType<ReportComponentProps<ReportData>>
  }>,
  retry: VoidFunction,
  name?: React.ReactNode
) {
  function ReportLoadingError() {
    return <LoadingReportError name={name} retry={retry} />
  }
  return lazyWithVersionCheck(importFn, ReportLoadingError)
}

interface CreateLoadableComponentProps<ReportData> extends LoadingReportProps {
  importFn: AsyncFunction<void, { default: React.ComponentType<ReportComponentProps<ReportData>> }>
}

function createLoadableComponent<ReportData>({ importFn, name, layout }: CreateLoadableComponentProps<ReportData>) {
  return function LazyRender(props: ReportComponentProps<ReportData>) {
    const [retryKey, setRetryKey] = React.useState(0)

    const LazyComponent = React.useMemo(
      () => retryLazyImport<ReportData>(importFn, () => setRetryKey(key => key + 1), name),
      [retryKey]
    )

    return (
      <React.Suspense fallback={<LoadingReport layout={layout} />}>
        <LazyComponent {...props} />
      </React.Suspense>
    )
  }
}

// map reports name with components (dynamic imports with specified loading component)
const REPORT_COMPONENT_SOURCES = {
  [ReportNames.BANK_TRANSACTIONS]: createLoadableComponent({
    importFn: () => import('./BankTransactionsReportAccordion'),
    name: <FormattedMessage id="pulse.report.name.balance" defaultMessage="Banki egyenlegek" />,
  }),
  [ReportNames.SETUP_TASK_LIST]: createLoadableComponent({
    importFn: () => import('./SetupTaskList'),
    name: <FormattedMessage id="pulse.report.name.setupTaskList" defaultMessage="Személyre szabás" />,
  }),
  [ReportNames.VAT_POSITION]: createLoadableComponent({
    importFn: () => import('./VatPositionReportAccordion'),
    name: <FormattedMessage id="pulse.report.name.vatPosition" defaultMessage="ÁFA pozíció" />,
  }),
  [ReportNames.TAXATION_AAM]: createLoadableComponent({
    importFn: () => import('./TaxationAamAccordion'),
    name: <FormattedMessage id="pulse.report.name.taxation.aam" defaultMessage="AAM keret" />,
  }),
  [ReportNames.TAXATION_KATA]: createLoadableComponent({
    importFn: () => import('./TaxationKataAccordion'),
    name: <FormattedMessage id="pulse.report.name.taxation.kata" defaultMessage="KATA keret" />,
  }),
  [ReportNames.TAXATION_ATALANY]: createLoadableComponent({
    importFn: () => import('./TaxationAtalanyAccordion'),
    name: <FormattedMessage id="pulse.report.name.taxation.atalany" defaultMessage="Átalányadó keret" />,
  }),
  [ReportNames.TAXATION_TAX_CALCULATION]: createLoadableComponent({
    importFn: () => import('./TaxationTaxCalculationAccordion'),
    name: <FormattedMessage id="pulse.report.name.taxation.taxCalculation" defaultMessage="Adókalkuláció" />,
  }),
  [ReportNames.TAXATION_TASK]: createLoadableComponent({
    importFn: () => import('./TaxationTasks/TaxationTask'),
    name: <FormattedMessage id="pulse.report.name.taxation.task" defaultMessage="Határidős feladat" />,
  }),
  //* utility cards
  [ReportNames.SPLITTER]: createLoadableComponent({
    importFn: () => import('./elements/Splitter'),
    name: 'splitter',
  }),
  [ReportNames.DISCLAIMER]: createLoadableComponent({
    importFn: () => import('./elements/Disclaimer'),
    name: 'disclaimer',
  }),
  [ReportNames.KPI_WIDGET]: createLoadableComponent({
    importFn: () => import('./KpiWidget'),
    name: <FormattedMessage id="pulse.kpiWidget.name" defaultMessage="Összesítő adatok" />,
    layout: ReportLayouts.THREE_COLUMN_WIDGET,
  }),
  [ReportNames.PULSE_MONTHLY_CHART]: createLoadableComponent({
    importFn: () => import('./PulseMonthlyChartAccordion'),
    name: <FormattedMessage id="pulse.monthlyChart.name" defaultMessage="Kiadások és bevételek" />,
    layout: ReportLayouts.ASYNC_ACCORDION_OPEN,
  }),
  [ReportNames.TAXATION_ATALANY_AUTOTAX]: createLoadableComponent({
    importFn: () => import('./TaxationAutotaxWidgets/TaxationAtalanyAutotaxWidget'),
    name: <FormattedMessage id="pulse.autotax.widget.name.atalany" defaultMessage="Átalányadó adózási összefoglaló" />,
    layout: ReportLayouts.TWO_COLUMN_WIDGET,
  }),
  [ReportNames.TAXATION_KATA_AUTOTAX]: createLoadableComponent({
    importFn: () => import('./TaxationAutotaxWidgets/TaxationKataAutotaxWidget'),
    name: <FormattedMessage id="pulse.autotax.widget.name.kata" defaultMessage="KATA adózási összefoglaló" />,
    layout: ReportLayouts.TWO_COLUMN_WIDGET,
  }),
} as const

interface ReportsContainerProps {
  companyCurrency: string
  reports: BackendReportData[]
  refetch: AsyncFunction
  updatedAt: string
}

function PureReportsContainer({ companyCurrency, reports, refetch, updatedAt }: ReportsContainerProps) {
  const location = useLocation()
  const navigate = useNavigate()

  const cardRefs = React.useRef<Record<string, HTMLDivElement | null>>({})

  const params = new URLSearchParams(location.search)
  const openCardId = params.get('card')

  // update URL when card is opened/closed and scroll into view when opened
  const handleOpen = React.useCallback(
    (cardId: string, isExpanding: boolean) => {
      const params = new URLSearchParams(location.search)

      if (isExpanding) {
        const isCurrentlyOpened = params.get('card') === cardId
        // when card already active just scroll into view
        if (isCurrentlyOpened) {
          cardRefs.current[cardId]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        } else {
          params.set('card', cardId)
          navigate({ search: params.toString() }, { replace: true, preventScrollReset: true })
        }
      } else {
        params.delete('card')
        navigate({ search: params.toString() }, { replace: true, preventScrollReset: true })
      }
    },
    [location.search, navigate]
  )

  // handle initial open (if no URL param set)
  const handleInitialOpen = React.useCallback(
    (cardId: string) => {
      const params = new URLSearchParams(location.search)
      const cardIdParam = params.get('card')

      if (cardIdParam && cardIdParam !== cardId) return

      handleOpen(cardId, true)
    },
    [handleOpen, location.search]
  )

  return (
    <>
      {reports.map(report => {
        const ReportComponent = REPORT_COMPONENT_SOURCES[report.name]
        if (!ReportComponent) return null
        const isOpen = openCardId === report.id
        return (
          <div
            key={report.id}
            ref={el => {
              cardRefs.current[report.id] = el
            }}
          >
            <ReportComponent
              companyCurrency={companyCurrency}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore - ReportComponentProps is not defined for all components
              report={report}
              refetchReports={refetch}
              updatedAt={updatedAt}
              open={isOpen}
              openHandler={handleOpen}
              initialOpenHandler={handleInitialOpen}
            />
          </div>
        )
      })}
    </>
  )
}

export const ReportsContainer = connect((state: Store) => ({
  companyCurrency: getDefaultCurrencyName(state),
}))(PureReportsContainer)

ReportsContainer.displayName = 'ReportsContainer'
